import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, UsersQueryParams } from '@/services';
import { CreateUserRequest, UpdateUserRequest } from '@/types/model';

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UsersQueryParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  activeUsersCount: (id: string) => [...userKeys.detail(id), 'active-users-count'] as const,
};

export const useUsers = (params: UsersQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => userService.getUsers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useUserList = (params: UsersQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: userKeys.list({ ...params, _view: 'list' }),
    queryFn: () => userService.getUserList(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useAppUsers = (params: UsersQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: [...userKeys.lists(), 'app', params],
    queryFn: () => userService.getAppUsers(params),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  });
};

export const useActiveUsersCountByCreator = (id: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: userKeys.activeUsersCount(id),
    queryFn: () => userService.getActiveUsersCountByCreator(id),
    enabled: !!id && enabled,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateUserRequest) =>
      userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userService.toggleUserStatus(userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};
